import type { Policy } from '@/types';
import { planKind } from './productCases';

type PlanKind = ReturnType<typeof planKind>;

/**
 * Each merchant's plan terms page on protega.ai (routes in routes/AppRoutes.tsx), by plan kind.
 * `any` is used for every plan kind the merchant sells under one terms page.
 * Add a merchant here when its terms page is published.
 */
const MERCHANT_TERMS: { match: RegExp; pages: Partial<Record<PlanKind | 'any', string>> }[] = [
  { match: /eume/, pages: { any: '/eume/accidental/tnc' } },
  { match: /izi\s*cart/, pages: { accidental: '/izicart/tnc', extended: '/izicart/extended/tnc' } },
  { match: /adivaa/, pages: { accidental: '/adivaa/accidental/tnc' } },
  { match: /nest\s*roots?/, pages: { accidental: '/nestroots/accidental/tnc' } },
  { match: /mecturin/, pages: { any: '/mecturing-productprotection/tnc' } },
  { match: /auto\s*furnish/, pages: { any: '/auto-furnish-productprotection/tnc' } },
  { match: /urban\s*flux/, pages: { any: '/urbanFlux/tnc' } },
  { match: /beach\s*company/, pages: { any: '/thebeachcompany/tnc' } },
];

/** Protega's general terms, for merchants or plan types without their own page yet. */
export const GENERAL_TERMS_PATH = '/terms';

/**
 * Terms page for a customer's plan: the merchant's own page for that plan type (e.g. EUME
 * Accidental Damage → /eume/accidental/tnc), else Protega's general terms.
 */
export function planTermsPath(policy: Policy): string {
  if (policy.merchantTncUrl) return policy.merchantTncUrl;
  const who = `${policy.merchantName ?? ''} ${policy.merchantUrl ?? ''}`.toLowerCase();
  const merchant = MERCHANT_TERMS.find((m) => m.match.test(who));
  const kind = planKind(policy.type);
  return merchant?.pages[kind] ?? merchant?.pages.any ?? GENERAL_TERMS_PATH;
}
